import React from 'react';
import { renderDate, renderMoney, dateRangeFilter } from "common/services/util"
import { IconButton, Tooltip } from "@material-ui/core";
import IvaSwitch from "invoices/components/IvaSwitch";
import DateRangeFilter from 'common/components/DateRangeFilter';

const normalize = (s) =>
    (s ?? '')
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

export const mapPaymentMode = {
    0: "Efectivo",
    1: "Tarjeta",
    2: "Transferencia",
    4: ""
}

export const MENSUAL = 10
export const TRIMESTRAL = 20
export const ANUAL = 30
export const NO_CREATION = 99

export const mapTypeCreation = {
    [MENSUAL]: "Mensual",
    [TRIMESTRAL]: "Trimestral",
    [ANUAL]: "Anual",
    [NO_CREATION]: "Puntual"
}

export const columnsInvoice = ({
    checkedSelected,
    checkAll,
    reference,
    searchClients,
    searchTasks,
    checkPaid,
    selectPaymentMode,
    paymentModeEditComponent,
    selectTypeCreation,
    isProforma
}) => {
    // Muestra 1–2 conceptos y añade "…" si hay más
    const renderConceptoCorto = (row) => {
        const text = (row.concept ?? '').toString().trim();
        if (!text) return '';
        const parts = text
            .split(/\r?\n|•|·|;|,|\s-\s/g)
            .map(s => s.trim())
            .filter(Boolean);
        if (parts.length <= 2) return parts.join(' - ');
        return `${parts[0]} - ${parts[1]}…`;
    };
    const columns = [
        {
            title: '',
            field: 'selected',
            render: props => checkedSelected(props),
            editable: 'never',
            cellStyle: { width: '30px' },
            filterComponent: () => checkAll(),
            searchable: false,
        },
        {
            title: 'Nº Factura',
            field: 'numInvoice',
            render: rowData => reference(rowData),
            editable: 'never',
            width: '190px',
            searchable: false,
        },
        {
            title: 'Cliente',
            field: 'nameClient',
            editComponent: ({ rowData, onRowDataChange }) => searchClients(rowData, onRowDataChange),
            width: '250px',
            searchable: false,
        },
        {
            title: 'CIF',
            field: 'dniClient',
            editable: 'never',
            width: '120px',
            searchable: false,
        },
        {
            title: 'Concepto',
            field: 'concept',
            width: '400px',
            render: renderConceptoCorto,
            searchable: false,
        },
        // Columna oculta para buscador global
        {
            title: 'Buscador',
            field: 'buscador',
            hidden: true,
            searchable: true,
            filtering: false,
            export: false,
            sorting: false,
            customFilterAndSearch: (term, row) => {
                const haystack = normalize([
                    row?.concept,
                    row?.nameClient,
                    row?.dniClient,
                    String(row?.numInvoice ?? ''),
                    row?.taskReference,
                    renderMoney(row?.totalPrice ?? ''),
                    renderMoney(row?.basePrice ?? ''),
                    renderDate(row?.invoiceDate ?? '', true),
                ].join(' '));

                const q = normalize(term || '');
                if (!q) return true;

                const tokens = [];
                const quoted = q.match(/"([^"]+)"/g) || [];
                quoted.forEach(m => tokens.push(m.replace(/"/g, '')));
                const qWithoutQuotes = q.replace(/"([^"]+)"/g, ' ').trim();
                qWithoutQuotes.split(/[,\s]+/).filter(Boolean).forEach(t => tokens.push(t));

                return tokens.every(t => {
                    const isNeg = t.startsWith('-');
                    const needle = normalize(isNeg ? t.slice(1) : t);
                    if (!needle) return true;
                    const has = haystack.includes(needle);
                    return isNeg ? !has : has;
                });
            },
        },
        {
            title: 'Fecha',
            field: 'invoiceDate',
            type: 'datetime',
            filterComponent: props => <DateRangeFilter {...props} />,
            customFilterAndSearch: (filter, rowData) => dateRangeFilter(filter, rowData.invoiceDate),
            render: rowData => renderDate(rowData ? rowData.invoiceDate : "", true),
            width: '160px',
            searchable: false,
        },
        {
            title: 'Total',
            field: 'totalPrice',
            render: rowData => renderMoney(rowData ? rowData.totalPrice : ""),
            align: 'right',
            editable: 'never',
            width: '100px',
            searchable: false,
        },
        {
            title: 'Base',
            field: 'basePrice',
            render: rowData => renderMoney(rowData ? rowData.basePrice : ""),
            align: 'right',
            editable: 'never',
            width: '100px',
            searchable: false,
        },
        {
            title: 'IVA',
            field: 'ivaPercent',
            type: 'numeric',
            render: rowData => (rowData.ivaPercent || 21) + " %",
            initialEditValue: 21,
            width: '25px',
            searchable: false,
        },
        {
            title: 'Pagado',
            field: 'payed',
            lookup: { 0: 'Sin pagar', 1: 'Pagado' },
            width: '25px',
            customFilterAndSearch: (term, rowData) => {
                if (term.length === 0 || term.length > 1) {
                    return true
                }
                return Boolean(parseInt(term[0])) === Boolean(rowData.payed)
            },
            render: rowData => checkPaid(rowData),
            editable: 'never',
            searchable: false,
        },
        {
            title: 'Tipo pago',
            field: 'paymentMode',
            lookup: mapPaymentMode,
            initialEditValue: 1,
            editComponent: paymentModeEditComponent,
            width: '100px',
            render: rowData =>
                <div style={{ width: '100px ' }}>
                    {rowData && selectPaymentMode({ rowData, mapPaymentMode, value: rowData.paymentMode })}
                </div>,
            searchable: false,
        },
        {
            title: 'Fecha pago',
            field: 'payedDate',
            filterComponent: props => <DateRangeFilter {...props} />,
            customFilterAndSearch: (filter, rowData) => dateRangeFilter(filter, rowData.payedDate),
            render: rowData => renderDate(rowData ? rowData.payedDate : ""),
            editable: 'never',
            width: '30px',
            searchable: false,
        },
        {
            title: 'Periodicidad',
            field: 'typeCreation',
            lookup: mapTypeCreation,
            initialEditValue: NO_CREATION,
            width: '100px',
            hidden: isProforma,
            render: rowData =>
                <div style={{ width: '100px ', display: 'flex', alignItems: 'center' }}>
                    {rowData && selectTypeCreation({ rowData, mapTypeCreation, value: rowData.typeCreation || NO_CREATION, disabled: !rowData.isPrincipal && rowData.autogenerated })}
                    {rowData.isPrincipal && <Tooltip title={`Factura recurrente principal desde ${renderDate(rowData.datePrincipal)}`}>
                        <IconButton>
                            🔁
                        </IconButton>
                    </Tooltip>}
                </div>,
            searchable: false,
        },
        {
            title: 'Factura principal',
            field: 'invoicePrincipalCode',
            editable: 'never',
            width: '25px',
            searchable: false,
            align: 'right',
            hidden: isProforma,
        },
        {
            title: 'Tarea',
            field: 'taskReference',
            editComponent: ({ rowData, onRowDataChange }) => searchTasks(rowData, onRowDataChange),
            width: '25px',
            searchable: false,
        }
    ]

    return columns
}

export const columnsInvoiceDetail = ({ textField, switchField, searchField, headerIvaChecked, toggleAllIva }) => {
    const columns = [
        {
            title: 'Referencia',
            field: 'reference',
            render: props => searchField(props)
        },
        {
            title: 'Descripción',
            field: 'description',
            render: props => textField(props, 'description'),
            cellStyle: {
                minWidth: '16vw'
            }
        },
        {
            title: 'Precio Ud.',
            field: 'unitPrice',
            render: props => textField(props, 'unitPrice', true, true)
        },
        {

            title: 'Uds.',
            field: 'units',
            render: props => textField(props, 'units', true),
        },
        {
            title: 'TOTAL',
            field: 'totalPrice',
            render: props => renderMoney(props.totalPrice)
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <span>IVA</span>
                    <IvaSwitch
                        checked={headerIvaChecked}
                        onChange={toggleAllIva}
                    />
                </div>
            ),
            field: 'iva',
            render: props => switchField(props, 'iva'),
            filtering: false,
            sorting: false,
        }
    ]

    return columns
}

const stateColors = {
    1: '#99ffcc', // Verde
}

export const getColorState = invoice => {
    if (invoice.payed)
        return stateColors[1];

    return '';
}